import { Toaster } from "react-hot-toast"
import Header from "../Header"
import { useTranslation } from "react-i18next"
import { useGetIssuanceRenewalIdForStaffFromContractedCompaniesById } from "../../../hooks/permit/query"
import useFormWithSchema from "../../../hooks/useFormWithSchema"
import { createRef, MutableRefObject, useEffect, useMemo, useRef, useState } from "react"
import { isCheckPermitApprovedByNextApprover, isPermitFieldsDisabled } from "../../../lib/functions"
import { APP_ROUTES } from "../../../routes"
import useAuthStore from "../../../store/Auth"
import { createIssuanceRenewalIdForStaffFromContractedCompaniesSchema } from "../../../schemas/issuanceRenewalIdForStaffFromContractedCompaniesSchema"
import Loader from "../../../components/loader"
import RequestingDepartmentInformation from "./RequestingDepartmentInformation"
import { useLocation } from "react-router-dom"
import { issuanceRenewalIdAttachmentKeys } from "../../../constants/common"
import Attachments from "./Attachments"
import ActionButtonGroup from "./ActionButtonGroup"

const IssuanceRenewalIdForStaffFromContractedCompanies = () => {
    const { t } = useTranslation()

    const location = useLocation()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const { user } = useAuthStore()

    const { data: issuanceRenewalId, isPending: isIssuanceRenewalIdLoading } =
        useGetIssuanceRenewalIdForStaffFromContractedCompaniesById({
            encryptedId: key || "",
        });

    const [issuanceRenewalIdDetails, setIssuanceRenewalIdDetails] = useState<{
        id: number;
        contractorEmployeeName: string;
        nationality: string;
        nationalId: string;
        nationalIdExpiryDate: string;
        dob: string;
        contractorsCompanyName: string;
        department: string;
        jobPosition: string;
        workStartDate: string;
        residentialAddress: string;
        contactNumber: string;
        cardValidityFrom: string;
        cardValidityUntil: string;
    }>()

    const [isAttachments, setIsAttachments] = useState({
        havePersonalPhoto: 0,
        haveNationalIdCopy: 0,
        haveContractorsAuthorizationLetter: 0,
    });

    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
        clearErrors,
        watch
    } = useFormWithSchema(createIssuanceRenewalIdForStaffFromContractedCompaniesSchema(isAttachments));

    const cardValidityFrom = new Date(watch("cardValidityFrom") || "");

    useEffect(() => {
        if (!issuanceRenewalId) {
            setIssuanceRenewalIdDetails(undefined);

            setValue("contractorEmployeeName", "");
            setValue("nationality", "");
            setValue("nationalId", "");
            setValue("nationalIdExpiryDate", "");
            setValue("dob", "");
            setValue("contractorsCompanyName", "");
            setValue("department", "");
            setValue("jobPosition", "");
            setValue("workStartDate", "");
            setValue("residentialAddress", "");
            setValue("contactNumber", "");
            setValue("cardValidityFrom", "");
            setValue("cardValidityUntil", "");

            setIsAttachments({
                havePersonalPhoto: 0,
                haveNationalIdCopy: 0,
                haveContractorsAuthorizationLetter: 0,
            });

            return
        }

        const issuanceRenewalIdFormatted = {
            id: issuanceRenewalId?.n_ContractorStaffIdRequestID,
            contractorEmployeeName: issuanceRenewalId?.s_ContractorEmployeeName,
            nationality: issuanceRenewalId?.s_Nationality,
            nationalId: issuanceRenewalId?.s_NationalID,
            nationalIdExpiryDate: issuanceRenewalId?.dt_NationalIDExpiryDate,
            dob: issuanceRenewalId?.dt_DateOfBirth,
            contractorsCompanyName: issuanceRenewalId?.s_ContractorCompanyName,
            department: issuanceRenewalId?.s_Department,
            jobPosition: issuanceRenewalId?.s_JobPosition,
            workStartDate: issuanceRenewalId?.dt_WorkStartDate,
            residentialAddress: issuanceRenewalId?.s_ResidentialAddress,
            contactNumber: issuanceRenewalId?.s_ContactNumber,
            cardValidityFrom: issuanceRenewalId?.dt_CardValidityFrom,
            cardValidityUntil: issuanceRenewalId?.dt_CardValidityUntil,
        }
        setIssuanceRenewalIdDetails(issuanceRenewalIdFormatted);

        Object.entries(issuanceRenewalIdFormatted).forEach(([key, value]) => {
            setValue(key as any, value);
        });

        setIsAttachments({
            havePersonalPhoto: issuanceRenewalId?.havePersonalPhotographFile,
            haveNationalIdCopy: issuanceRenewalId?.haveNationalIDCopyFile,
            haveContractorsAuthorizationLetter: issuanceRenewalId?.haveAuthorizationLetterFile,
        })
    }, [issuanceRenewalId, setValue]);


    const uploadRefs = useRef<
        Record<string, MutableRefObject<HTMLInputElement | null>>
    >(
        issuanceRenewalIdAttachmentKeys.reduce((acc, key) => {
            acc[key] = createRef<HTMLInputElement>();
            return acc;
        }, {} as Record<string, MutableRefObject<HTMLInputElement | null>>)
    );

    const [fileNames, setFileNames] = useState<Record<string, string>>(
        issuanceRenewalIdAttachmentKeys.reduce(
            (acc, key) => ({ ...acc, [key]: "" }),
            {}
        )
    );

    const setFileName = (key: string, value: React.SetStateAction<string>) => {
        setFileNames((prev) => ({
            ...prev,
            [key]: typeof value === "function" ? value(prev[key]) : value,
        }));
    };

    const isPermitApprovedByNextApprover = useMemo(
        () => isCheckPermitApprovedByNextApprover(location.pathname, APP_ROUTES.UPDATE_ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES, issuanceRenewalId?.n_CurrentApproverOrderNo),
        [location.pathname, issuanceRenewalId?.n_CurrentApproverOrderNo]
    );

    const isFieldsDisabled = useMemo(
        () => isPermitFieldsDisabled(issuanceRenewalId, isPermitApprovedByNextApprover, location.pathname, APP_ROUTES.APPROVE_ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES, user?.n_RoleID),
        [isPermitApprovedByNextApprover, location.pathname, user?.n_RoleID, issuanceRenewalId]
    );

    return (
        <>
            <Toaster position="top-right" reverseOrder={false} />

            <div className="flex flex-col gap-5">
                <Header title={t("issuanceRenewalIdForStaffFromContractedCompanies")} data={issuanceRenewalId} />

                {key && isIssuanceRenewalIdLoading ? (
                    <div className="flex justify-center">
                        <Loader />
                    </div>
                ) : (
                    <>
                        <RequestingDepartmentInformation
                            control={control}
                            errors={errors}
                            isFieldsDisabled={isFieldsDisabled}
                            cardValidityFrom={cardValidityFrom}
                        />

                        <Attachments
                            control={control}
                            errors={errors}
                            clearErrors={clearErrors}
                            isAttachments={isAttachments}
                            uploadRefs={uploadRefs.current}
                            fileNames={fileNames}
                            setFileName={setFileName}
                            isFieldsDisabled={isFieldsDisabled}
                            issuanceRenewalIdDetails={issuanceRenewalIdDetails}
                        />

                        <ActionButtonGroup
                            issuanceRenewalId={issuanceRenewalId}
                            isPermitApprovedByNextApprover={isPermitApprovedByNextApprover}
                            isFieldsDisabled={isFieldsDisabled}
                            handleSubmit={handleSubmit}
                        />
                    </>
                )}
            </div>

        </>
    )
}

export default IssuanceRenewalIdForStaffFromContractedCompanies
